import { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import { useTheme, Button, Tab, Tabs, Tooltip } from '@mui/material';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { PageLayout } from '../../PageLayout';
import { TabPanel } from '../../Atoms/TabPanel';
import {
  getSelectedTab,
  updateState,
  ApiAdvancedFilter,
  FilterableField,
  PaginatedResponse
} from '../../../store/GeneralStore';
import { fetchAssets, getCruxAssetsSubject, Asset } from '../../../store/Asset';
import {
  getCustomizationSubject,
  getCustomViewSubject,
  CustomView,
  PrefKey,
  UserCustomizations
} from '../../../store/UserCustomizations';
import { getCurrentUser } from '../../../store/User';
import { getRpmRunSubject } from '../../../store/RpmRun';
import { DefaultAssetGridColumnDefs } from './AssetGridColumnDefs';
import { UpstackDataGrid } from '../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { ViewsModal } from '../../Compounds/CustomTableViews/ViewsModal';
import { NewViewModal } from '../../Compounds/CustomTableViews/NewViewModal';
import { Menu as CustomViewMenu } from '../../Compounds/CustomTableViews/Menu';
import {
  buildAdvancedFilterSet,
  getColsForTab,
  getInitialState,
  mergeAndDeduplicateFilters
} from '../../Compounds/UpstackDataGrid/helpers';
import { DEFAULT_TAB_SX_STYLES } from '../../../utils/styleHelpers';
import { getFiltersFromSearchParams, clearFiltersFromSearchParams } from '../../../utils/helpers';

export interface TabProps {
  name: string;
  customView?: boolean;
  filter?: FilterableField[];
  tooltip?: string;
  disabledFilters?: string[];
  isValidationView?: boolean;
}

export const AssetPage = () => {
  const [assetsLoading, setAssetsLoading] = useState<boolean>(false);
  const [assets, setAssets] = useState<PaginatedResponse<Asset>>();
  const [userCustomizations, setUserCustomizations] = useState<UserCustomizations | undefined>();
  const [selectedTab, setSelectedTab] = useState(getSelectedTab().assets);
  const [runDate, setRunDate] = useState<Date | null>(null);
  const [customViews, setCustomViews] = useState<CustomView[]>([]);
  const [customViewNames, setCustomViewNames] = useState<string[]>([]);
  const [selectedCustomView, setSelectedCustomView] = useState<string>('');
  const [selectedValidationView, setSelectedValidationView] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [triggerSave, setTriggerSave] = useState<boolean>(false);
  const [newlyCreatedViewName, setNewlyCreatedViewName] = useState<string | null>(null);

  const theme = useTheme();
  const filterKeys = useMemo(
    () =>
      DefaultAssetGridColumnDefs()
        .filter((column) => column.filterable)
        .map((column) => column.field),
    []
  );

  useEffect(() => {
    const rpmRunSub = getRpmRunSubject().subscribe((run) => {
      if (run && run.runLastDayOfMonth) {
        setRunDate(new Date(run.runLastDayOfMonth));
      }
    });

    return () => {
      if (rpmRunSub) rpmRunSub.unsubscribe();
    };
  }, []);

  const parsedFilters = useMemo(() => getFiltersFromSearchParams(filterKeys), [selectedTab]);

  const defaultTabs = useMemo(
    () =>
      [
        {
          name: 'Mine',
          filter: [{ operator: 'equals', field: 'commissionsExperienceAnalystEmail', value: getCurrentUser()?.email }],
          tooltip: `CEA equals ${getCurrentUser()?.email}`,
          disabledFilters: ['commissionsExperienceAnalyst', 'commissionsExperienceAnalystEmail']
        },
        {
          name: 'My Open BnC',
          filter: [
            { operator: 'equals', field: 'commissionsExperienceAnalystEmail', value: getCurrentUser()?.email },
            {
              operator: 'isAnyOf',
              field: 'commissionsStatus',
              value: ['Pending Commissions', 'Partially Commissioning']
            },
            ...(runDate
              ? [{ operator: 'onOrBefore', field: 'expectedFirstCommissionRun', value: runDate.toISOString() }]
              : []),
            { operator: 'is', field: 'openCommissionsTicket', value: false }
          ],
          tooltip: `CEA equals ${getCurrentUser()?.email}
      Commissions Status is any of Pending Commissions, Partially Commissioning
      EFCR is before ${runDate?.toDateString()}
      Open Ticket equals No
      `,
          disabledFilters: [
            'commissionsExperienceAnalyst',
            'commissionsExperienceAnalystEmail',
            'commissionsStatus',
            'openCommissionsTicket'
          ]
        },
        {
          name: 'Open BnC',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'commissionsStatus',
              value: ['Pending Commissions', 'Partially Commissioning']
            },
            ...(runDate
              ? [{ operator: 'onOrBefore', field: 'expectedFirstCommissionRun', value: runDate.toISOString() }]
              : []),
            { operator: 'is', field: 'openCommissionsTicket', value: false }
          ],
          tooltip: `Commissions Status is any of Pending Commissions, Partially Commissioning
      EFCR is before ${runDate?.toDateString()}
      Open Ticket equals No
      `,
          disabledFilters: ['commissionsStatus', 'openCommissionsTicket']
        },
        {
          name: 'BnC',
          filter: [
            {
              operator: 'isAnyOf',
              field: 'commissionsStatus',
              value: ['Pending Commissions', 'Partially Commissioning']
            }
          ],
          tooltip: 'Commissions Status is any of Pending Commissions, Partially Commissioning',
          disabledFilters: ['commissionsStatus']
        },
        {
          name: 'Commissioning',
          filter: [
            { operator: 'isAnyOf', field: 'commissionsStatus', value: ['Commissioning', 'Partially Commissioning'] }
          ],
          tooltip: 'Commissions Status is any of Commissioning, Partially Commissioning',
          disabledFilters: ['commissionsStatus']
        },
        {
          name: 'All',
          filter: [],
          tooltip: 'Unfiltered',
          disabledFilters: []
        }
      ] as TabProps[],
    [runDate]
  );

  const validationViews = useMemo(
    () =>
      [
        {
          name: 'Missing Link',
          filter: [
            { field: 'commissionsStatus', operator: 'isAnyOf', value: ['Commissioning', 'Partially Commissioning'] },
            { field: 'rpmAccountCount', operator: '=', value: 0 }
          ],
          tooltip: `Commissions Status is any of Commissioning, Partially Commissioning
      RPM Account Count equals 0
      `,
          disabledFilters: ['commissionsStatus', 'rpmAccountCount'],
          isValidationView: true
        },
        {
          name: 'Missing Run',
          filter: [
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Cancelled' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Non-Commissionable' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Legacy Archive' },
            { field: 'expectedFirstCommissionRun', operator: 'isEmpty', value: undefined },
            { field: 'actualFirstCommissionRun', operator: 'isEmpty', value: undefined }
          ],
          tooltip: `Commission status does not equal Cancelled
      Commissions status does not equal Non-Commissionable
      Commissions status does not equal Legacy Archive
      EFCR is empty
      AFCR is empty
      `,
          disabledFilters: ['commissionsStatus', 'expectedFirstCommissionRun', 'actualFirstCommissionRun'],
          isValidationView: true
        },
        {
          name: 'Disconnected Not Decommissioned',
          filter: [
            { field: 'assetStatus', operator: 'equals', value: 'Disconnected' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Decommissioned' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Cancelled' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Non-Commissionable' },
            { field: 'commissionsStatus', operator: 'doesNotEqual', value: 'Legacy Archive' },
            ...(runDate
              ? [{ operator: 'onOrBeforeOrEmpty', field: 'expectedDecommissionDate', value: runDate.toISOString() }]
              : [])
          ],
          tooltip: `Service Status equals Disconnected
      Commissions Status does not equal Decommissioned
      Commissions Status does not equal Cancelled
      Commissions Status does not equal Non-Commissionable
      Commissions Status does not equal Legacy Archive
      Expected Decommission Date is before ${runDate?.toDateString()} or empty
      `,
          disabledFilters: ['assetStatus', 'commissionsStatus'],
          isValidationView: true
        },
        {
          name: 'Validation Needed',
          filter: [
            { field: 'commissionsValidationStatus', operator: 'isEmpty', value: undefined },
            { field: 'commissionsStatus', operator: 'equals', value: 'Commissioning' }
          ],
          tooltip: `Commissions Validation Status is empty
      Commissions Status equals Commissioning
      `,
          disabledFilters: ['commissionsValidationStatus', 'commissionsStatus'],
          isValidationView: true
        },
        {
          name: 'Original Still Commissioning',
          filter: [
            { field: 'parentCommissionsStatus', operator: 'equals', value: 'Commissioning' },
            { field: 'quoteType', operator: 'doesNotEqual', value: 'Replace' },
            { field: 'commissionsStatus', operator: 'equals', value: 'Commissioning' }
          ],
          tooltip: `Parent Commissions Status equals Commissioning.
      Commissions Status equals Commissioning.
      Quote Type does not equal Replace.
      `,
          disabledFilters: ['parentCommissionsStatus', 'quoteType', 'commissionsStatus'],
          isValidationView: true
        },
        {
          name: 'Original and Renewal Overlap',
          filter: [
            { field: 'commissionsStatus', operator: 'isAnyOf', value: ['Commissioning', 'Partially Commissioning'] },
            { field: 'firstRunBeforeParentDecommission', operator: 'is', value: 'true' },
            { field: 'quoteType', operator: 'doesNotEqual', value: 'Replace' }
          ],
          tooltip: `Commissions Status is any of Commissioning, Partially Commissioning.
      Act. First Run is earlier than Parent Asset Decommission Date.
      Quote Type does not equal Replace.
      `,
          disabledFilters: ['commissionsStatus', 'firstRunBeforeParentDecommission'],
          isValidationView: true
        },
        {
          name: 'First Run Before Sign',
          filter: [{ field: 'firstRunBeforeSign', operator: 'is', value: 'true' }],
          tooltip: 'Act. First Run is earlier than Contract Sign Date.',
          disabledFilters: ['firstRunBeforeSign'],
          isValidationView: true
        }
      ] as TabProps[],
    [runDate]
  );

  const allAvailableViews = useMemo(() => {
    const customViewsTabProps: TabProps[] = customViews
      .filter((view) => view.page === 'assetGrid_')
      .map((t) => ({ name: t.name, customView: true }));

    return [...defaultTabs, ...validationViews, ...customViewsTabProps];
  }, [defaultTabs, validationViews, customViews]);

  const mainTabsToDisplay = useMemo(() => {
    return defaultTabs.filter((tab) => !tab.isValidationView);
  }, [defaultTabs]);

  const validationMenuOptions = useMemo(() => {
    return validationViews.map((view) => {
      return { name: view.name, tooltip: view.tooltip };
    });
  }, [validationViews]);

  const handleFetchAssets = useCallback(
    async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
      if (localLoading) setAssetsLoading(true);

      let currentTabFilters: FilterableField[] = [];
      const currentSelectedView = allAvailableViews[selectedTab];

      if (currentSelectedView) {
        currentTabFilters = (currentSelectedView.filter as FilterableField[]) || [];
      }

      await fetchAssets(
        {
          ...filters,
          filters: mergeAndDeduplicateFilters(filters?.filters || [], currentTabFilters, true)
        },
        forceRefresh
      );
      if (localLoading) setAssetsLoading(false);
    },
    [allAvailableViews, selectedTab]
  );

  const handleTabChange = useCallback(
    (index: number, name: string, type: 'main' | 'custom' | 'validation') => {
      if (type === 'main') {
        setSelectedCustomView('');
        setSelectedValidationView('');
      } else if (type === 'custom') {
        setSelectedCustomView(name);
        setSelectedValidationView('');
      } else if (type === 'validation') {
        setSelectedValidationView(name);
        setSelectedCustomView('');
      }

      const actualIndex = allAvailableViews.findIndex((tab) => {
        if (type === 'custom') {
          return tab.name === name && tab.customView;
        } else if (type === 'validation') {
          return tab.name === name && tab.isValidationView;
        } else {
          return tab.name === name && !tab.customView && !tab.isValidationView;
        }
      });

      if (actualIndex !== -1) {
        updateState({
          selectedTab: {
            ...getSelectedTab(),
            assets: actualIndex
          }
        });
        setSelectedTab(actualIndex);
      }
    },
    [allAvailableViews]
  );

  const getInitialSort = useCallback(
    (userCust: UserCustomizations) => {
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `assetGrid_${pageName}` as PrefKey;
      const savedSort = userCust?.content?.[currentPage]?.muiConfig?.sorting?.sortModel;
      return savedSort?.length ? savedSort : ([] as GridSortModel);
    },
    [allAvailableViews, selectedTab]
  );

  const getInitialFilter = useCallback(
    (userCust: UserCustomizations) => {
      if (parsedFilters.length > 0) {
        clearFiltersFromSearchParams();
        return { items: parsedFilters, logicOperator: 'and' } as GridFilterModel;
      }
      const currentTab = allAvailableViews[selectedTab];
      let pageName = currentTab?.name || '';
      if (currentTab && !currentTab.customView) {
        pageName = pageName.replace(/\s/g, '');
      }
      const currentPage = `assetGrid_${pageName}` as PrefKey;
      const savedFilter = userCust?.content?.[currentPage]?.muiConfig?.filter?.filterModel;
      return savedFilter || ({} as GridFilterModel);
    },
    [allAvailableViews, selectedTab]
  );

  const dataFetch = useCallback(() => {
    if (userCustomizations && allAvailableViews[selectedTab]) {
      const initialSortModel = getInitialSort(userCustomizations);
      const initialFilterModel = getInitialFilter(userCustomizations);
      handleFetchAssets(
        {
          ...buildAdvancedFilterSet(initialFilterModel || ({} as GridFilterModel), filterKeys),
          orders: initialSortModel?.length
            ? initialSortModel.map((sort) => ({
                sort: sort.field,
                direction: sort.sort
              }))
            : undefined
        },
        true,
        true
      );
    }
  }, [
    userCustomizations,
    allAvailableViews,
    selectedTab,
    getInitialSort,
    getInitialFilter,
    handleFetchAssets,
    filterKeys
  ]);

  const handleNewViewCreated = useCallback((viewName: string) => {
    setNewlyCreatedViewName(viewName);
  }, []);

  useEffect(() => {
    const assetSub = getCruxAssetsSubject().subscribe((a) => setAssets(a));
    const userCustSub = getCustomizationSubject().subscribe((userCust) => setUserCustomizations(userCust));
    const customViewsSub = getCustomViewSubject().subscribe((custViews) => setCustomViews(custViews));
    return () => {
      if (assetSub) assetSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
      if (customViewsSub) customViewsSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setCustomViewNames(customViews.filter((view) => view.page === 'assetGrid_').map((v) => v.name));
  }, [customViews]);

  useEffect(() => {
    if (allAvailableViews.length > 0 && selectedTab !== undefined && selectedTab >= 0) {
      const initialSelectedView = allAvailableViews[selectedTab];
      if (initialSelectedView) {
        if (initialSelectedView.customView) {
          setSelectedCustomView(initialSelectedView.name);
          setSelectedValidationView('');
        } else if (initialSelectedView.isValidationView) {
          setSelectedValidationView(initialSelectedView.name);
          setSelectedCustomView('');
        } else {
          setSelectedCustomView('');
          setSelectedValidationView('');
        }
      }
    }
  }, [allAvailableViews, selectedTab]);

  useEffect(() => {
    if (newlyCreatedViewName) {
      const newViewIndex = allAvailableViews.findIndex((tab) => tab.name === newlyCreatedViewName && tab.customView);
      if (newViewIndex !== -1) {
        handleTabChange(newViewIndex, newlyCreatedViewName, 'custom');
        setNewlyCreatedViewName(null);
      }
    }
  }, [newlyCreatedViewName, allAvailableViews, handleTabChange]);

  useEffect(() => {
    if (runDate && userCustomizations && allAvailableViews.length > 0) {
      dataFetch();
    }
  }, [selectedTab, userCustomizations, runDate, allAvailableViews, dataFetch]);

  useEffect(() => {
    if (selectedCustomView && !customViewNames.includes(selectedCustomView)) {
      setSelectedCustomView('');
      const allTabIndex = allAvailableViews.findIndex(
        (tab) => tab.name === 'All' && !tab.customView && !tab.isValidationView
      );
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
    const validationViewNames = validationMenuOptions.map((view) => view.name);
    if (selectedValidationView && !validationViewNames.includes(selectedValidationView)) {
      setSelectedValidationView('');
      const allTabIndex = allAvailableViews.findIndex(
        (tab) => tab.name === 'All' && !tab.customView && !tab.isValidationView
      );
      handleTabChange(allTabIndex !== -1 ? allTabIndex : 0, 'All', 'main');
    }
  }, [
    selectedCustomView,
    selectedValidationView,
    customViewNames,
    validationMenuOptions,
    allAvailableViews,
    handleTabChange
  ]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleNewTabModalOpen = () => {
    setCreateOpen(true);
  };

  const handleCustomViewMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.customView);
      if (index !== -1) {
        handleTabChange(index, name, 'custom');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  const handleValidationMenuChange = useCallback(
    (name: string) => {
      const index = allAvailableViews.findIndex((tab) => tab.name === name && tab.isValidationView);
      if (index !== -1) {
        handleTabChange(index, name, 'validation');
      }
    },
    [allAvailableViews, handleTabChange]
  );

  return (
    <PageLayout
      dataCy="asset-page"
      dataTestId="asset-page"
      pageTitle="Assets">
      <Fragment>
        <Tabs
          sx={DEFAULT_TAB_SX_STYLES}
          value={selectedTab}
          scrollButtons="auto"
          onChange={(_e, index) => {
            const currentTab = allAvailableViews[index];
            if (currentTab) {
              if (currentTab.customView) {
                handleTabChange(index, currentTab.name, 'custom');
              } else if (currentTab.isValidationView) {
                handleTabChange(index, currentTab.name, 'validation');
              } else {
                handleTabChange(index, currentTab.name, 'main');
              }
            }
          }}
          slotProps={{
            indicator: {
              sx: {
                backgroundColor: theme.palette.primary.main,
                height:
                  allAvailableViews[selectedTab]?.customView || allAvailableViews[selectedTab]?.isValidationView
                    ? 0
                    : 2,
                borderRadius: 1
              }
            }
          }}>
          {mainTabsToDisplay.map((tab, _index) => (
            <Tab
              sx={{
                color: 'grey9',
                fontSize: '.8rem',
                textTransform: 'none'
              }}
              key={tab.name}
              label={
                <Tooltip
                  title={<span style={{ whiteSpace: 'pre-line' }}>{tab.tooltip}</span>}
                  slotProps={{
                    tooltip: {
                      sx: {
                        maxWidth: 'none',
                        width: 'auto'
                      }
                    }
                  }}>
                  <span>{tab.name}</span>
                </Tooltip>
              }
            />
          ))}
          <div className={`flex ${selectedValidationView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="validation-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedValidationView}
                selectedView={selectedValidationView}
                viewOptions={validationMenuOptions}
                handleViewChange={handleValidationMenuChange}
                defaultTitle="Validation"
              />
            </div>
          </div>
        </Tabs>
        <div className="-mt-12 float-right inline-flex pr-4">
          <div className={`flex ${selectedCustomView ? 'border-b-2 border-indigo' : ''}`}>
            <div
              key="custom-views"
              className="self-center">
              <CustomViewMenu
                setSelectedView={setSelectedCustomView}
                selectedView={selectedCustomView}
                viewOptions={customViewNames}
                handleViewChange={handleCustomViewMenuChange}
                defaultTitle="My Views"
              />
            </div>
          </div>
          <div className="ml-3 self-center">
            <Button
              onClick={() => setTriggerSave(true)}
              startIcon={<AddCircleIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Save As View
            </Button>
          </div>
          <div
            className="ml-3 self-center"
            key="add-custom-view">
            <Button
              onClick={() => setOpen(true)}
              startIcon={<EditNoteIcon />}
              sx={{
                fontSize: '.8rem',
                textTransform: 'none'
              }}>
              Edit Views
            </Button>
          </div>
        </div>
        {allAvailableViews.map((tab, index) => {
          let tabKeyName = tab.name || '';
          if (tab && !tab.customView) {
            tabKeyName = tabKeyName.replace(/\s/g, '');
          }
          const tabKey = `assetGrid_${tabKeyName}` as PrefKey;
          const showHiddenFields = tab.isValidationView ? ['lastCommissionRun'] : [];
          const colsForTab = getColsForTab(DefaultAssetGridColumnDefs({ showHiddenFields }), tab.disabledFilters ?? []);
          const gridSettingsForTab = userCustomizations?.content?.[tabKey];
          let initialState;
          if (gridSettingsForTab) {
            initialState = getInitialState(gridSettingsForTab, colsForTab, tabKey);
            const initialFilterModel = getInitialFilter(userCustomizations);
            if (initialState && initialFilterModel) {
              if (initialState.filter) {
                initialState.filter.filterModel = initialFilterModel;
              } else {
                initialState.filter = { filterModel: initialFilterModel };
              }
            }
          }

          return (
            <Fragment key={tab.name}>
              {!initialState ||
                (assetsLoading && (
                  <div className="flex h-[45rem] m-auto">
                    <ComponentSpinner label="Loading data..." />
                  </div>
                ))}
              {!assetsLoading && initialState && (
                <TabPanel
                  key={tab.name}
                  index={index}
                  value={selectedTab}>
                  <UpstackDataGrid
                    advancedFiltering={true}
                    apiFilter={true}
                    columns={colsForTab}
                    fetchFunction={handleFetchAssets}
                    filterKeys={filterKeys}
                    gridSettings={gridSettingsForTab}
                    handleNewTabModalOpen={handleNewTabModalOpen}
                    initialState={initialState}
                    page={tabKey}
                    rows={assets?.data || []}
                    rowCount={assets?.totalCount}
                    showSearch={false}
                    title={`Assets - ${tab.name}`}
                    triggerSave={triggerSave}
                    handleSaveComplete={() => setTriggerSave(false)}
                  />
                </TabPanel>
              )}
            </Fragment>
          );
        })}
      </Fragment>
      <ViewsModal
        open={open}
        handleClose={handleClose}
        type="asset"
      />
      <NewViewModal
        onViewCreated={handleNewViewCreated}
        open={createOpen}
        setOpen={setCreateOpen}
        sourceTab={allAvailableViews[selectedTab]?.name || 'All'}
        sourceTabFilter={allAvailableViews[selectedTab]?.filter}
        type="asset"
      />
    </PageLayout>
  );
};
